import { markTipSeen } from "frontend/api";
import { useBoardValue } from "frontend/state/board-state";
import tracking from "frontend/tracking";
import { useSetAtom } from "jotai";
import { useCallback, useMemo } from "react";
import { TipName } from "shared/datamodel/schemas";
import { userTipsAtom } from "state-atoms";

export enum CIMOnboardingSteps {
  DEVICE_SELECTION = "device-selection",
  MOUSE_DRAG = "mouse-drag",
  MOUSE_ZOOM = "mouse-zoom",
  TRACKPAD_SLIDE = "trackpad-slide",
  TRACKPAD_ZOOM = "trackpad-zoom",
  BASIC_TOOLS = "basic-tools",
  TEMPLATES = "templates",
}

export const CONTROLS_PHASE = new Set([
  CIMOnboardingSteps.DEVICE_SELECTION,
  CIMOnboardingSteps.MOUSE_DRAG,
  CIMOnboardingSteps.MOUSE_ZOOM,
  CIMOnboardingSteps.TRACKPAD_SLIDE,
  CIMOnboardingSteps.TRACKPAD_ZOOM,
]);

export const BASIC_TOOLS_PHASE = new Set([CIMOnboardingSteps.BASIC_TOOLS]);

export const TEMPLATES_PHASE = new Set([CIMOnboardingSteps.TEMPLATES]);

export function getInitialStepId(phase: TipName | null) {
  if (phase === TipName.cimOnboardingDeviceSelection) return CIMOnboardingSteps.DEVICE_SELECTION;
  if (phase === TipName.cimOnboardingBasicTools) return CIMOnboardingSteps.BASIC_TOOLS;
  if (phase === TipName.cimOnboardingTemplates) return CIMOnboardingSteps.TEMPLATES;
  return null;
}

const CIM_ONBOARDING_PHASES = [
  TipName.cimOnboardingDeviceSelection,
  TipName.cimOnboardingBasicTools,
  TipName.cimOnboardingTemplates,
];

export function useGetCIMOnboardingPhases() {
  const boardValue = useBoardValue() ?? [];
  const isBoardOwner = !!boardValue?.[0]?.board?.isOwner;

  const filterPhase = useCallback(
    (phase: TipName): boolean => {
      if (phase === TipName.cimOnboardingDeviceSelection) return true;
      if (phase === TipName.cimOnboardingBasicTools) return true;
      if (phase === TipName.cimOnboardingTemplates) return isBoardOwner;
      return true;
    },
    [isBoardOwner]
  );

  return useMemo(() => CIM_ONBOARDING_PHASES.filter(filterPhase), [filterPhase]);
}

export function useOnPhaseFinished() {
  const setUserTips = useSetAtom(userTipsAtom);

  return useCallback(
    (currentStep: string | null) => {
      if (!currentStep) return;

      if (CONTROLS_PHASE.has(currentStep as CIMOnboardingSteps)) {
        markTipSeen(TipName.cimOnboardingDeviceSelection);
        setUserTips((previous) => new Set([...previous, TipName.cimOnboardingDeviceSelection]));
        tracking.trackAnalyticsEvent(`onboarding_cim_device_selection_finished`);
      }

      if (BASIC_TOOLS_PHASE.has(currentStep as CIMOnboardingSteps)) {
        markTipSeen(TipName.cimOnboardingBasicTools);
        setUserTips((previous) => new Set([...previous, TipName.cimOnboardingBasicTools]));
        tracking.trackAnalyticsEvent(`onboarding_cim_basic_tools_finished`);
      }

      if (TEMPLATES_PHASE.has(currentStep as CIMOnboardingSteps)) {
        markTipSeen(TipName.cimOnboardingTemplates);
        setUserTips((previous) => new Set([...previous, TipName.cimOnboardingTemplates]));
        tracking.trackAnalyticsEvent(`onboarding_cim_templates_finished`);
      }
    },
    [setUserTips]
  );
}
